import { PureComponent } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import {
  get,
  find,
  isEqual,
  isFinite,
  toNumber,
} from 'lodash';
import withRouter from '../../../wrappers/withRouter';
import {
  tablePropsType,
  tablePropsDefault,
} from '../../../types/tablePropsType';
import {
  getSettings,
  getFiltersFromUrl,
  getData,
} from '../../../helpers/Table';
import {
  getParam,
} from '../../../helpers/Navigation';
import {
  list as mediaFormatList,
  getIcon as getMediaFormatIcon,
  getTooltip as getMediaFormatTooltip,
} from '../../../helpers/MediaFormat';
import {
  list as segmentTypeList,
} from '../../../helpers/SegmentType';
import {
  list as segmentCreativeList,
} from '../../../helpers/SegmentCreative';
import {
  list as spotTypeList,
} from '../../../helpers/SpotType';
import {
  list as spotSelectionStrategyList,
} from '../../../helpers/SpotSelectionStrategy';
import {
  list as cpmTypeList,
} from '../../../helpers/CpmType';
import {
  convertToTwentyFourHour,
} from '../../../utils/TimeUtil';
import {
  format as formatCurrency,
} from '../../../utils/CurrencyUtil';
import {
  getCurrencies,
} from '../../../actions/Currency/CurrencyActions';
import API from '../../../api';
import VibeTable from '../VibeTable';
import VibeModal from '../../VibeModal/VibeModal';
import VibeAlert from '../../VibeAlert/VibeAlert';
import CellTooltip from '../CellTypes/CellTooltip';
import LabelCell from '../CellTypes/LabelCell';
import TextCell from '../CellTypes/TextCell';
import IconCell from '../CellTypes/IconCell';
import TimestampCell from '../CellTypes/TimestampCell';
import MissingCreatives from '../../MissingCreatives/MissingCreatives';
import DaySelector from '../../DaySelector/DaySelector';
import Progress from '../../Progress/Progress';
import VibeButton from '../../VibeButton/VibeButton';
import VibeIcon from '../../VibeIcon/VibeIcon';
import viDownload from '../../../icons/viDownload';
import viSpeaker from '../../../icons/viSpeaker';
import * as color from '../../../sass/color.scss';
import './TableSegments.scss';

const tableId = 'table:segments';
const permissionPrefix = 'segment';

class TableSegments extends PureComponent {
  constructor(props) {
    super(props);

    const {
      columns: columnNames,
      defaultSortBy,
      pageSize,
      paginatorProps: {
        urlPaging,
        urlFilters,
      },
      currencies,
      adProvider,
    } = props;

    const columnsDef = [{
      name: '.',
      defaultWidth: 40,
    },
    {
      name: 'Name',
      searchAttr: 'name',
      defaultWidth: 275,
      resizable: true,
      searchable: true,
      sortable: true,
      // Removed autoFocus due to automatic scrolling of the Order Details page
      // autoFocus: true,
    },
    {
      name: 'Segment Type',
      searchAttr: 'segmentType',
      defaultWidth: 175,
      resizable: true,
      searchable: true,
      sortable: true,
      dropdownItems: segmentTypeList({
        includeEmpty: true,
        emptyData: {
          placeholder: 'Search...',
        },
      }),
    },
    {
      name: 'Creative',
      searchAttr: 'missingCreatives',
      defaultWidth: 170,
      searchable: adProvider === 'vibenomics',
      sortable: true,
      resizable: true,
      dropdownItems: [
        {
          label: '',
          value: '',
          placeholder: 'Search...',
        },
        ...segmentCreativeList(adProvider === 'vibenomics'),
      ],
    },
    {
      name: 'Impression Progress',
      searchAttr: 'deliveredImpressions',
      defaultWidth: 275,
      resizable: true,
      searchable: false,
      sortable: false,
    },
    {
      name: 'Media Format',
      searchAttr: 'mediaFormat',
      icon: viSpeaker,
      defaultWidth: 95,
      resizable: false,
      searchable: true,
      sortable: true,
      locked: true,
      dropdownItems: mediaFormatList({
        includeEmpty: true,
        emptyData: {
          placeholder: 'Search...',
        },
      }),
    },
    {
      name: 'Start Date',
      searchAttr: 'startDate',
      defaultWidth: 175,
      datepicker: true,
      searchable: true,
      sortable: true,
      resizable: true,
    },
    {
      name: 'End Date',
      searchAttr: 'endDate',
      defaultWidth: 175,
      datepicker: true,
      searchable: true,
      sortable: true,
      resizable: true,
    },
    {
      name: 'Cancelled Date',
      searchAttr: 'cancellationDate',
      defaultWidth: 175,
      datepicker: true,
      searchable: true,
      sortable: true,
      resizable: true,
    },
    {
      name: 'Created By',
      searchAttr: 'createdByUserName',
      defaultWidth: 175,
      resizable: true,
      searchable: true,
      sortable: true,
    },
    {
      name: 'Created Date',
      searchAttr: 'createdDate',
      defaultWidth: 175,
      datepicker: true,
      searchable: true,
      sortable: true,
      resizable: true,
    },
    {
      name: 'Start Time',
      searchAttr: 'startTime',
      defaultWidth: 175,
      resizable: true,
      sortable: true,
    },
    {
      name: 'End Time',
      searchAttr: 'endTime',
      defaultWidth: 175,
      resizable: true,
      sortable: true,
    },
    {
      name: 'Budget',
      searchAttr: 'budget',
      defaultWidth: 175,
      resizable: true,
      sortable: true,
    },
    {
      name: 'CPM',
      searchAttr: 'cpm',
      defaultWidth: 175,
      resizable: true,
      sortable: true,
    },
    {
      name: 'CPM Type',
      searchAttr: 'cpmType',
      defaultWidth: 225,
      resizable: true,
      searchable: true,
      sortable: true,
      dropdownItems: cpmTypeList({
        includeEmpty: true,
        emptyData: {
          placeholder: 'Search...',
        },
      }),
    },
    {
      name: 'Target Impressions',
      searchAttr: 'targetImpressions',
      defaultWidth: 175,
      resizable: true,
      sortable: true,
    },
    {
      name: 'Total Impressions',
      searchAttr: 'totalImpressions',
      defaultWidth: 175,
      resizable: true,
      sortable: true,
    },
    {
      name: 'Total Spots',
      searchAttr: 'totalSpots',
      defaultWidth: 175,
      resizable: true,
      sortable: true,
    },
    {
      name: 'Frequency',
      searchAttr: 'frequency',
      defaultWidth: 175,
      resizable: true,
      sortable: true,
    },
    {
      name: 'Frequency Cap',
      searchAttr: 'frequencyCap',
      defaultWidth: 175,
      resizable: true,
      sortable: true,
    },
    {
      name: 'Spot Type',
      searchAttr: 'spotType',
      defaultWidth: 175,
      resizable: true,
      searchable: true,
      sortable: true,
      dropdownItems: spotTypeList({
        includeEmpty: true,
        emptyData: {
          placeholder: 'Search...',
        },
      }),
    },
    {
      name: 'Spot Selection Strategy',
      searchAttr: 'spotSelectionStrategy',
      defaultWidth: 275,
      resizable: true,
      searchable: true,
      sortable: true,
      dropdownItems: spotSelectionStrategyList({
        includeEmpty: true,
        emptyData: {
          placeholder: 'Search...',
        },
      }),
    },
    {
      name: 'Allocate Remainder',
      searchAttr: 'allocateRemainder',
      defaultWidth: 315,
      resizable: true,
      searchable: true,
      sortable: true,
      dropdownItems: [
        {
          label: '',
          value: '',
        },
        {
          label: 'Enabled',
          value: 'true',
        },
        {
          label: 'Disabled',
          value: 'false',
        },
      ],
    },
    {
      name: 'Allow Duplicate Ads Back to Back',
      searchAttr: 'allowBackToBackAdDelivery',
      defaultWidth: 315,
      resizable: true,
      searchable: true,
      sortable: true,
      dropdownItems: [
        {
          label: '',
          value: '',
          placeholder: 'Search...',
        },
        {
          label: 'Enabled',
          value: 'true',
        },
        {
          label: 'Disabled',
          value: 'false',
        },
      ],
    },
    {
      name: 'Days of Week',
      searchAttr: 'daysOfWeek',
      defaultWidth: 275,
      resizable: true,
    },
    {
      name: 'Currency',
      searchAttr: 'currencyCode',
      defaultWidth: 190,
      resizable: true,
      searchable: true,
      sortable: true,
      dropdownItems: [
        {
          label: '',
          value: '',
          placeholder: 'Search...',
        },
        ...currencies.map(currency => {
          return {
            label: currency.label,
            value: currency.key,
          };
        }),
      ],
    },
    {
      name: '...',
      defaultWidth: 72,
    }];

    const settings = getSettings({
      tableId,
      columnsDef,
      columnNames,
    });

    this.state = {
      columnsDef,
      columnNames,
      rows: [],
      loading: true,
      totalItems: 0,
      active: !urlFilters || (urlFilters && getParam('active') !== 'false'),
      pageNumber: urlPaging
        ? parseInt(getParam('page') || 1, 10)
        : 1,
      pageSize: get(settings, 'pageSize', pageSize),
      sortBy: {
        label: get(settings, 'sortBy.label', defaultSortBy.label),
        attr: get(settings, 'sortBy.attr', defaultSortBy.attr),
        direction: get(settings, 'sortBy.direction', defaultSortBy.direction),
      },
      filters: urlFilters
        ? getFiltersFromUrl({ columns: columnsDef })
        : {},
      confirm: false,
      confirmRow: {},
      confirmAction: '',
      confirmTitle: '',
      confirmText: '',
      confirmApproveColor: '',
      // only load currencies if the column exists
      currenciesFetched: !columnNames.includes('Currency') || currencies.length > 0,
    };

    // listen for when sidebar data changes
    document.addEventListener('onSaveSegment', this.onUpdateTableData);
  }

  componentDidMount() {
    const {
      getCurrencies,
    } = this.props;

    this.getData();

    // get supported currencies
    getCurrencies();
  }

  componentDidUpdate(prevProps) {
    const {
      fetch,
      collection,
      currencies,
      columns,
    } = this.props;

    const {
      collection: prevCollection,
      currencies: prevCurrencies,
      columns: prevColumns,
    } = prevProps;

    if (!fetch && !isEqual(collection, prevCollection)) {
      this.onUpdate({
        refresh: true,
      });
    }

    // allow updating of columns based on filters
    if (!isEqual(columns, prevColumns)) {
      this.setState({
        columnNames: columns,
      });
    }

    // Currencies fetched, update the search dropdown
    if (currencies.length > 0 && prevCurrencies.length <= 0) {
      this.setState((state) => {
        const {
          columnsDef,
        } = state;

        const currencyColumn = find(columnsDef, { name: 'Currency' });
        currencyColumn.dropdownItems = [
          {
            label: '',
            value: '',
            placeholder: 'Search...',
          },
          ...currencies.map(currency => {
            return {
              label: currency.label,
              value: currency.key,
            };
          }),
        ];

        return {
          columnsDef,
          currenciesFetched: true,
        };
      });
    }
  }

  componentWillUnmount() {
    document.removeEventListener('onSaveSegment', this.onUpdateTableData);
  }

  /**
   * When an event asks the table to update the table data
   */
  onUpdateTableData = () => {
    this.onUpdate({
      refresh: true,
    });
  };

  onSelectMenuItem = (itemName, _rowId) => {
    const {
      rows,
    } = this.state;

    const row = find(rows, { _rowId });

    if (!row) {
      console.error('Row not found matching ID', _rowId);
      return;
    }

    switch (itemName) {
      case 'archive': {
        this.setState({
          confirm: true,
          confirmAction: itemName,
          confirmRow: row,
          confirmTitle: 'Archive Segment',
          confirmText: `Are you sure you want to archive ${row.name}?`,
          confirmApproveColor: 'error',
        });

        break;
      }

      case 'unarchive': {
        this.setState({
          confirm: true,
          confirmAction: itemName,
          confirmRow: row,
          confirmTitle: 'Unarchive Segment',
          confirmText: `Are you sure you want to unarchive ${row.name}?`,
          confirmApproveColor: 'warning',
        });

        break;
      }

      default:
        break;
    }
  };

  onConfirmModal = async () => {
    const {
      collection,
      onRemove,
    } = this.props;

    const {
      confirmAction,
      confirmRow: {
        _id,
        flightId,
      },
    } = this.state;

    if (!_id) {
      console.error('onConfirmModal no row ID');
      return;
    }

    switch (confirmAction) {
      case 'archive': {
        const response = await API.Flight.Segment.deactivate({ flightId, _id });

        const successText = 'FLIGHTSEGMENT.DEACTIVATED';
        const success = get(response, '[0].type') === successText;

        if (success) {
          // if using a local collection, remove from the table
          if (collection.length > 0) {
            const items = collection.filter(item => item._id === _id);

            items.forEach((item) => {
              // remove the item from the table
              onRemove(item);
            });
          }

          // tell listening components to update the counts and table data
          this.onUpdate({
            refresh: true,
          });
        }

        break;
      }

      case 'unarchive': {
        const response = await API.Flight.Segment.reactivate({ flightId, _id });

        const successText = 'FLIGHTSEGMENT.REACTIVATED';
        const success = get(response, '[0].type') === successText;

        if (success) {
          // tell listening components to update the counts and table data
          this.onUpdate({
            refresh: true,
          });
        }

        break;
      }

      default:
        break;
    }

    this.resetConfirmModal();
  };

  /**
   * When the Bulk Archive Completes
   */
  onBulkArchive = () => {
    // tell listening components to update the counts and table data
    this.onUpdate({
      refresh: true,
    });
  };

  /**
   * Reset to Default Settings
   */
  onReset = () => {
    const {
      defaultSortBy,
      pageSize,
    } = this.props;

    this.onUpdate({
      refresh: true,
      data: {
        sortBy: defaultSortBy,
        pageSize: pageSize || 50,
      },
    });
  };

  onUpdate = ({
    data,
    refresh = false,
  }) => {
    if (refresh) {
      // refresh the table data
      this.setState(data, this.getData);
    } else {
      this.setState(data);
    }
  };

  onExport = () => {
    this.getData({
      export: true,
    });
  };

  getData = async (config = {}) => {
    const {
      props,
      state,
    } = this;

    if (!state.loading && !config.export) {
      this.setState({
        loading: true,
      });
    }

    const {
      rows,
      filters,
      totalItems,
    } = await getData({
      props,
      state,
      config,
    });

    if (rows) {
      this.setState({
        loading: false,
        rows,
        totalItems,
      });

      props.onFetchComplete({
        rows,
        filters,
        totalItems,
      });
    }
  };

  /**
   * Get label style for the message type cell
   */
  getEnabledDisabledStyle = (value) => {
    if (value) {
      return {
        text: 'Enabled',
        textStyle: {
          background: color.success16,
          color: color.success,
        },
      };
    }

    return {
      text: 'Disabled',
      textStyle: {
        background: color.error16,
        color: color.error,
      },
    };
  };

  /**
   * Reset the confirm modal data
   */
  resetConfirmModal = () => {
    this.setState({
      confirm: false,
    });
  };

  // get string value for Missing Creative
  getCreativeString = (value, adProvider) => {
    if (adProvider !== 'vibenomics') {
      return 'N/A';
    }
    switch (value) {
      case true:
        return 'Missing';

      case false:
        return 'Attached';

      default:
        return 'Missing';
    }
  };

  renderCell = ({
    column,
    row,
  }) => {
    const {
      segmentSummaries,
      adProvider,
    } = this.props;

    // get the attribute with data for the cell
    const attr = column.valueAttr || column.searchAttr;
    const value = get(row, attr, '');

    switch (column.name) {
      case 'Name': {
        const segmentSummary = find(segmentSummaries, { _id: row._id }) || {};

        return (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
            }}
          >
            {get(segmentSummary, 'warnings.length', 0) > 0 && (
              <VibeAlert
                style={{
                  marginRight: 8,
                  fontSize: 12,
                }}
                severity="warning"
                alerts={segmentSummary.warnings || []}
                size={24}
                iconOnly
              />
            )}

            <CellTooltip title={value}>
              <div
                style={{
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {value}
              </div>
            </CellTooltip>
          </div>
        );
      }

      case 'Media Format':
        return (
          <IconCell
            rowId={row._rowId}
            icon={getMediaFormatIcon(value)}
            color={color.manatee}
            tooltip={getMediaFormatTooltip(value)}
            tooltipProps={{
              placement: 'right',
            }}
          />
        );

      case 'Start Date':
      case 'End Date':
        return (
          <TimestampCell
            time={value}
            format="YYYY-MM-DD"
          />
        );
      case 'Cancelled Date':
      case 'Created Date':
        return (
          <TimestampCell
            time={value}
            format="YYYY-MM-DD HH:mm:ss"
          />
        );

      case 'Start Time':
      case 'End Time': {
        const formatValue = convertToTwentyFourHour({
          time: value,
        });

        return (
          <CellTooltip title={formatValue}>
            <div className="cell-content">
              {formatValue}
            </div>
          </CellTooltip>
        );
      }

      case 'Budget':
      case 'CPM': {
        const formatValue = formatCurrency(value, row.currencyCode);

        return (
          <CellTooltip title={formatValue}>
            <div className="cell-content">
              {formatValue}
            </div>
          </CellTooltip>
        );
      }

      case 'CPM Type': {
        const cpmTypeLabel = find(cpmTypeList(), { value })?.label || value;

        return (
          <TextCell
            text={cpmTypeLabel}
          />
        );
      }

      case 'Target Impressions':
      case 'Total Impressions':
      case 'Spots':
      case 'Frequency': {
        const formatValue = toNumber(value).toLocaleString();

        return (
          <CellTooltip title={formatValue}>
            <div className="cell-content">
              {formatValue}
            </div>
          </CellTooltip>
        );
      }

      case 'Spot Type':
      case 'Spot Selection Strategy':
      case 'Segment Type': {
        const formatValue = value.replace(/-/g, ' ');
        return (
          <CellTooltip title={formatValue}>
            <div
              className="cell-content"
              style={{
                textTransform: 'capitalize',
              }}
            >
              {formatValue}
            </div>
          </CellTooltip>
        );
      }

      case 'Frequency Cap': {
        const freqCapVal = !get(row, 'frequencyCapped', '') ? 'N/A' : value;

        return (
          <CellTooltip title={freqCapVal}>
            <div className="cell-content">
              {freqCapVal}
            </div>
          </CellTooltip>
        );
      }

      case 'Creative':
        return (
          <MissingCreatives
            status={this.getCreativeString(value, adProvider)}
          />
        );

      case 'Impression Progress': {
        if (row.orderType === 'open-exchange') {
          const isValueNumber = isFinite(value);
          const useValue = isValueNumber
            ? value.toLocaleString()
            : value;

          return (
            <div className="cell-content">
              {useValue}
            </div>
          );
        }

        return (
          <Progress
            value={row.deliveredImpressions}
            valueMax={row.targetImpressions}
            pacing={row.pacingPercent}
          />
        );
      }

      case 'Allocate Remainder': {
        const {
          text,
          textStyle,
        } = this.getEnabledDisabledStyle(value);

        return (
          <LabelCell
            value={text}
            background={textStyle.background}
            color={textStyle.color}
            style={{
              textTransform: 'uppercase',
            }}
            inline
          />
        );
      }

      case 'Allow Duplicate Ads Back to Back': {
        const {
          text,
          textStyle,
        } = this.getEnabledDisabledStyle(value);

        return (
          <LabelCell
            value={text}
            background={textStyle.background}
            color={textStyle.color}
            style={{
              textTransform: 'uppercase',
            }}
            inline
          />
        );
      }

      case 'Days of Week':
        return (
          <DaySelector
            dayFormat="dd"
            selectedDays={value}
            disabled
          />
        );

      case 'Currency': {
        const {
          currencies,
        } = this.props;

        return (
          <div className="cell-content">
            {find(currencies, { key: value })?.label || value}
          </div>
        );
      }

      default:
        return (
          <CellTooltip title={value}>
            <div className="cell-content">
              {value}
            </div>
          </CellTooltip>
        );
    }
  };

  render() {
    const {
      className,
      toolbarProps,
      csv,
    } = this.props;

    const {
      columnsDef,
      columnNames,
      rows,
      loading,
      totalItems,
      active,
      pageNumber,
      pageSize,
      sortBy,
      filters,
      confirm,
      confirmTitle,
      confirmText,
      confirmApproveColor,
      currenciesFetched,
    } = this.state;

    const totalItemsStr = totalItems.toLocaleString();
    const customButtons = [];

    if (!currenciesFetched) {
      return null;
    }

    if (csv) {
      customButtons.push(
        <VibeButton
          key="btn-export"
          variant="outlined"
          text="Export"
          color="secondary"
          icon={(
            <VibeIcon
              icon={viDownload}
              color={color.primary}
              size={16}
            />
          )}
          tooltip={`Export ${totalItemsStr} Segments to CSV`}
          tooltipProps={{
            placement: 'top',
          }}
          onClick={this.onExport}
        />,
      );
    }

    // add export button
    const newToolbarProps = {
      ...toolbarProps,
      customButtons: [
        ...customButtons,
        ...get(toolbarProps, 'customButtons', []),
      ],
    };

    return (
      <div className={classNames('Table', 'TableSegments', className)}>
        <VibeTable
          {...this.props}
          toolbarProps={newToolbarProps}
          tableId={tableId}
          columnsDef={columnsDef}
          columnNames={columnNames}
          rows={rows}
          loading={loading}
          permissionPrefix={permissionPrefix}
          sortBy={sortBy}
          filters={filters}
          totalItems={totalItems}
          active={active}
          pageNumber={pageNumber}
          pageSize={pageSize}
          renderCell={this.renderCell}
          onSelectMenuItem={this.onSelectMenuItem}
          onBulkArchive={this.onBulkArchive}
          onReset={this.onReset}
          onUpdate={this.onUpdate}
        />

        <VibeModal
          show={confirm}
          type="confirm"
          confirmProps={{
            color: confirmApproveColor,
          }}
          title={confirmTitle}
          text={confirmText}
          onConfirm={this.onConfirmModal}
          onClose={this.resetConfirmModal}
        />
      </div>
    );
  }
}

function mapStateToProps(state) {
  return {
    currencies: state.currency.list,
  };
}

const mapDispatchToProps = {
  getCurrencies,
};

TableSegments.propTypes = {
  columns: PropTypes.arrayOf(PropTypes.string).isRequired,
  segmentSummaries: PropTypes.arrayOf(PropTypes.oneOfType([
    PropTypes.object,
  ])),
  ...tablePropsType,
};

TableSegments.defaultProps = {
  segmentSummaries: [],
  ...tablePropsDefault,
};

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(TableSegments));
